import type { InputState } from '../shared/protocol';

export interface InputHandlers {
  onToggleChat: () => void;
  onToggleChar: () => void;
  /** The `I` key - equipment and pack. */
  onToggleGear: () => void;
  onToggleMute: () => void;
  onEscape: () => void;
  onFirstGesture: () => void;
  /** Quick bar slots 1-3. */
  onHotbar: (slot: number) => void;
  /** The `E` key - talk to the trader. */
  onInteract: () => void;
  /** The two class abilities, on Q and R. */
  onAbility: (slot: 0 | 1) => void;
  /** The `P` key - party and friends. */
  onToggleSocial: () => void;
  /** The `F` key - frame rate and entity counts. */
  onTogglePerf: () => void;
  /** The `G` key - low graphics mode for weaker machines. */
  onToggleQuality: () => void;
}

const MOVE_KEYS = new Set([
  'KeyW',
  'KeyA',
  'KeyS',
  'KeyD',
  'ArrowUp',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
]);

/** Keyboard + mouse state. Aiming is resolved by the caller against the camera. */
export class Input {
  readonly keys = new Set<string>();
  /** Mouse position in CSS pixels relative to the canvas. */
  mouseX = 0;
  mouseY = 0;
  zoom = 1;

  private mouseDown = false;
  private suppressUntil = 0;
  private handlers: InputHandlers;
  chatOpen = false;
  enabled = true;
  /** False while on the login screen, so typing a name is never intercepted. */
  active = false;

  constructor(private canvas: HTMLCanvasElement, handlers: InputHandlers) {
    this.handlers = handlers;
    this.mouseX = window.innerWidth / 2;
    this.mouseY = window.innerHeight / 2;

    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('blur', this.onBlur);
    canvas.addEventListener('mousemove', this.onMouseMove);
    canvas.addEventListener('mousedown', this.onMouseDown);
    window.addEventListener('mouseup', this.onMouseUp);
    canvas.addEventListener('wheel', this.onWheel, { passive: false });
    canvas.addEventListener('contextmenu', (e) => e.preventDefault());
  }

  destroy(): void {
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('blur', this.onBlur);
    window.removeEventListener('mouseup', this.onMouseUp);
    this.canvas.removeEventListener('mousemove', this.onMouseMove);
    this.canvas.removeEventListener('mousedown', this.onMouseDown);
    this.canvas.removeEventListener('wheel', this.onWheel);
  }

  /** True when the event is going into a text field and must be left alone. */
  private isTypingTarget(target: EventTarget | null): boolean {
    if (!(target instanceof HTMLElement)) return false;
    const tag = target.tagName;
    return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable;
  }

  private onKeyDown = (e: KeyboardEvent): void => {
    // Never steal keystrokes from a text box - that would make it impossible to
    // type a name (or a chat message) containing W, A, S or D.
    if (this.isTypingTarget(e.target)) {
      if (e.code === 'Escape') this.handlers.onEscape();
      return;
    }
    if (!this.active) return;

    if (e.repeat) {
      if (MOVE_KEYS.has(e.code) || e.code === 'Space') e.preventDefault();
      return;
    }
    this.handlers.onFirstGesture();

    switch (e.code) {
      case 'Enter':
        // While the chat box is open, let the form's submit handler send it.
        if (this.chatOpen) return;
        this.handlers.onToggleChat();
        e.preventDefault();
        return;
      case 'Escape':
        this.handlers.onEscape();
        e.preventDefault();
        return;
      case 'KeyC':
        if (!this.chatOpen) {
          this.handlers.onToggleChar();
          e.preventDefault();
        }
        return;
      case 'KeyI':
        if (!this.chatOpen) {
          this.handlers.onToggleGear();
          e.preventDefault();
        }
        return;
      case 'KeyE':
        if (!this.chatOpen) {
          this.handlers.onInteract();
          e.preventDefault();
        }
        return;
      case 'KeyP':
        if (!this.chatOpen) {
          this.handlers.onToggleSocial();
          e.preventDefault();
        }
        return;
      case 'KeyF':
        if (!this.chatOpen) {
          this.handlers.onTogglePerf();
          e.preventDefault();
        }
        return;
      case 'KeyG':
        if (!this.chatOpen) {
          this.handlers.onToggleQuality();
          e.preventDefault();
        }
        return;
      case 'Digit1':
      case 'Digit2':
      case 'Digit3':
        if (!this.chatOpen) {
          this.handlers.onHotbar(Number(e.code.slice(-1)));
          e.preventDefault();
        }
        return;
      case 'KeyQ':
        if (!this.chatOpen) {
          this.handlers.onAbility(0);
          e.preventDefault();
        }
        return;
      case 'KeyR':
        if (!this.chatOpen) {
          this.handlers.onAbility(1);
          e.preventDefault();
        }
        return;
      case 'KeyM':
        if (!this.chatOpen) {
          this.handlers.onToggleMute();
          return;
        }
        break;
      default:
        break;
    }

    this.keys.add(e.code);
    if (MOVE_KEYS.has(e.code) || e.code === 'Space') e.preventDefault();
  };

  private onKeyUp = (e: KeyboardEvent): void => {
    this.keys.delete(e.code);
  };

  private onBlur = (): void => {
    this.keys.clear();
    this.mouseDown = false;
  };

  private onMouseMove = (e: MouseEvent): void => {
    const rect = this.canvas.getBoundingClientRect();
    this.mouseX = e.clientX - rect.left;
    this.mouseY = e.clientY - rect.top;
  };

  private onMouseDown = (e: MouseEvent): void => {
    this.handlers.onFirstGesture();
    // Swallow the click that dismissed the chat box so it does not attack.
    if (performance.now() < this.suppressUntil) return;
    if (e.button === 0) this.mouseDown = true;
  };

  /** Ignore mouse clicks for a moment (used when a UI element steals focus). */
  suppressClick(ms = 250): void {
    this.suppressUntil = performance.now() + ms;
    this.mouseDown = false;
  }

  private onMouseUp = (e: MouseEvent): void => {
    if (e.button === 0) this.mouseDown = false;
  };

  private onWheel = (e: WheelEvent): void => {
    e.preventDefault();
    const next = this.zoom * (e.deltaY > 0 ? 0.9 : 1.1);
    this.zoom = Math.max(0.55, Math.min(1.8, next));
  };

  get up(): boolean {
    return this.enabled && (this.keys.has('KeyW') || this.keys.has('ArrowUp'));
  }

  get down(): boolean {
    return this.enabled && (this.keys.has('KeyS') || this.keys.has('ArrowDown'));
  }

  get left(): boolean {
    return this.enabled && (this.keys.has('KeyA') || this.keys.has('ArrowLeft'));
  }

  get right(): boolean {
    return this.enabled && (this.keys.has('KeyD') || this.keys.has('ArrowRight'));
  }

  get attack(): boolean {
    return this.enabled && (this.mouseDown || this.keys.has('Space'));
  }

  /** Snapshot of the current input, ready to send to the server. */
  snapshot(aim: number): InputState {
    return {
      up: this.up,
      down: this.down,
      left: this.left,
      right: this.right,
      aim,
      attack: this.attack,
    };
  }
}
